/**
 * Rôles opérateur : sous-types métier (+ `operators` si encore présent en base / JWT).
 * Aligné avec public.user_roles.role_type (Supabase).
 */

export const OPERATOR_SUBROLE_TYPES = [
  'operator_3d',
  'operator_thermo',
  'operator_finition',
  'operator_qualite',
];

/** Ancien rôle ; conservé pour compatibilité lecture (assignations non migrées). */
export const LEGACY_OPERATOR_ROLE_TYPE = 'operators';

/**
 * Rôles « pilotage production / logistique » : mêmes filtres et écrans que le responsable production.
 * Ordre = priorité d’affichage « primaire » si l’utilisateur a plusieurs rôles listés.
 */
export const PRODUCTION_MANAGER_ROLE_TYPES = ['production_responsible', 'logistics_coordinator'];

export function isProductionManagerRole(roleType) {
  if (!roleType || typeof roleType !== 'string') return false;
  return PRODUCTION_MANAGER_ROLE_TYPES.includes(roleType);
}

/** Tous les role_type qui héritent des accès « opérateur » (routes, sidebar, cas assignés). */
export const OPERATOR_FAMILY_ROLE_TYPES = [...OPERATOR_SUBROLE_TYPES, LEGACY_OPERATOR_ROLE_TYPE];

/** Poste mobile expédition (même écran atelier que les opérateurs). */
export const SHIPPING_STATION_ROLE_TYPE = 'livraison';

/** Rôles autorisés sur `/atelier-lot` (QR + saisie). */
export const WORKSHOP_ATELIER_ROLE_TYPES = [...OPERATOR_FAMILY_ROLE_TYPES, SHIPPING_STATION_ROLE_TYPE];

/**
 * Postes « une phase à la fois » sur l’atelier (hors legacy `operators` = vue élargie).
 * @param {string} roleType
 */
export function isSinglePhaseWorkshopStation(roleType) {
  if (!roleType || typeof roleType !== 'string') return false;
  if (roleType === SHIPPING_STATION_ROLE_TYPE) return true;
  return OPERATOR_SUBROLE_TYPES.includes(roleType);
}

/** Poste mobile : opérateurs + expédition (pour login / garde-fous UI). */
export const MOBILE_WORKSTATION_ROLE_TYPES = [...OPERATOR_FAMILY_ROLE_TYPES, SHIPPING_STATION_ROLE_TYPE];

/**
 * Utilisateur n’ayant que des rôles poste mobile (atelier / livraison), sans rôle bureau.
 * @param {string[]} roleTypes
 */
export function userIsMobileWorkstationOnly(roleTypes) {
  const list = Array.isArray(roleTypes) ? [...new Set(roleTypes.filter(Boolean))] : [];
  if (list.length === 0) return false;
  return list.every((r) => MOBILE_WORKSTATION_ROLE_TYPES.includes(r));
}

/**
 * Accès à `/production-tracking-dashboard` (même logique OR que ProtectedRoute : rôle pilote ou permission).
 * @param {string[]} roleTypes
 * @param {string[]} permissionCodes
 */
export function userCanAccessProductionTrackingDashboard(roleTypes, permissionCodes) {
  const roles = Array.isArray(roleTypes) ? roleTypes : [];
  const perms = Array.isArray(permissionCodes) ? permissionCodes : [];
  if (roles.includes('administrator')) return true;
  if (PRODUCTION_MANAGER_ROLE_TYPES.some((r) => roles.includes(r))) return true;
  if (WORKSHOP_ATELIER_ROLE_TYPES.some((r) => roles.includes(r))) return true;
  const deskAndAdmin = ['nav.production.tracking', 'nav.production.workshop'];
  return perms.some((c) => typeof c === 'string' && deskAndAdmin.includes(c.trim()));
}

export function isOperatorFamilyRole(roleType) {
  if (!roleType || typeof roleType !== 'string') return false;
  return OPERATOR_FAMILY_ROLE_TYPES.includes(roleType);
}

/** Vrai si l’utilisateur a au moins un rôle opérateur (famille). */
export function userHasOperatorFamilyRole(roleTypes) {
  const list = Array.isArray(roleTypes) ? roleTypes : [];
  return list.some((r) => isOperatorFamilyRole(r));
}

/** Opérateurs atelier + poste livraison (jalons sur l’écran lot). */
export function isWorkshopAtelierParticipantRole(roleType) {
  if (!roleType || typeof roleType !== 'string') return false;
  return isOperatorFamilyRole(roleType) || roleType === SHIPPING_STATION_ROLE_TYPE;
}

/** Priorité dashboard / profil : même rang que l’ancien `operators`. */
export const OPERATOR_PRIMARY_ROLE_PRIORITY = [...OPERATOR_SUBROLE_TYPES, LEGACY_OPERATOR_ROLE_TYPE];
